import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { AppState } from "src/app/core/app.state";
import { CustomersService } from "../services/customers.services";
import { CommonService } from "src/app/shared/common.service";
import * as customersActions  from "./customers.actions"
import { catchError, finalize, map, mergeMap, of, switchMap, tap } from "rxjs";
import { APIResponse } from "src/app/core/models/api-response";
import { ICountry, ICurrency, ICustomer, ILocation, IPayment_term, IState } from "../models/customers.models";
import { AlertEnums } from "src/app/shared/enums/shared.enums";
import { TranslateService } from "@ngx-translate/core";
import { DEFAULT_PAGE_SIZE } from "src/app/config/global.config";

@Injectable()
export class CustomersEffects {
  constructor(
    private actions$: Actions,
    public store: Store<AppState>,
    private customersServices: CustomersService,
    private commonService: CommonService,
    private translateService: TranslateService
  ) { }

      // fetch All Customers
      FetchAllCustomersEffect$ = createEffect(() =>
        this.actions$.pipe(
          ofType(customersActions.FetchAllCustomers),
          tap(() => {
            this.store.dispatch(customersActions.SetCustomersLoader({ isLoading: true }));
          }),
          switchMap(() =>
            this.customersServices.listAllCustomers().pipe(
              map((res: APIResponse<ICustomer>) => customersActions.FetchAllCustomersSuccess({ customers: res })),
              catchError(() => of(
                customersActions.FetchAllCustomersError()
              )),
              finalize(() => {
                this.store.dispatch(customersActions.SetCustomersLoader({ isLoading: false }));
              })
            )
          ),
        )
      );

  // fetch Customers
  FetchCustomersEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customersActions.FetchCustomers),
      tap(() => {
        this.store.dispatch(customersActions.SetCustomersLoader({ isLoading: true }));
      }),
      switchMap((action) =>
          this.customersServices.listCustomers(action.page, action.filter, action.key, action.sort_by, action.sort_mode, action.subsidiary_id, action.deleted).pipe(
            map((res: APIResponse<ICustomer>) => customersActions.FetchCustomersSuccess({ customers: res })),
            catchError(() => of(
              customersActions.FetchCustomersError()
            )),
            finalize(() => {
              this.store.dispatch(customersActions.SetCustomersLoader({ isLoading: false }));
            })
          ),

      ),
    )
  );

      // get Customer by id
      GetCustomerByIdEffect$ = createEffect(() =>
        this.actions$.pipe(
          ofType(customersActions.GetCustomerById),
          tap(() => {
            this.store.dispatch(customersActions.SetCustomersLoader({ isLoading: true }));
          }),
          switchMap((action) =>
            this.customersServices.getCustomerById(action.id).pipe(
              map((res: APIResponse<ICustomer>) => customersActions.GetCustomerByIdSuccess({ customer: res.object!})),
              catchError(() => of(
                customersActions.GetCustomerByIdError()
              )),
              finalize(() => {
                this.store.dispatch(customersActions.SetCustomersLoader({ isLoading: false }));
              })
            )
          )
        )
      )

      // get Customer by id
      GetCompanyByIdEffect$ = createEffect(() =>
        this.actions$.pipe(
          ofType(customersActions.GetCompanyById),
          tap(() => {
            this.store.dispatch(customersActions.SetCompanyLoader({ isLoading: true }));
          }),
          switchMap((action) =>
            this.customersServices.getCompanyById(action.id).pipe(
              map((res: APIResponse<ICustomer>) => customersActions.GetCompanyByIdSuccess({ company: res.object!})),
              catchError(() => of(
                customersActions.GetCompanyByIdError()
              )),
              finalize(() => {
                this.store.dispatch(customersActions.SetCompanyLoader({ isLoading: false }));
              })
            )
          )
        )
      )
    // add Customer
    AddCustomerEffect$ = createEffect(() =>
      this.actions$.pipe(
        ofType(customersActions.AddCustomer),
        tap(() => {
          this.store.dispatch(customersActions.SetCustomersLoader({ isLoading: true }));
        }),
        switchMap((action) =>
          this.customersServices.addCustomer(action.customer).pipe(
            map((res: APIResponse<ICustomer>) => customersActions.AddCustomerSuccess({ customer: res.data[0] })),
            tap(() => {
              this.commonService.showToast(
                this.translateService.instant(`alerts.${AlertEnums.companyAddSuccess}`),
                'SUCCESS'
              );
            }),
            catchError((error) =>
              of(
                customersActions.AddCustomerError()
              )
            ),
            finalize(() => {
              this.store.dispatch(customersActions.SetCustomersLoader({ isLoading: false }));
            })
          )
        )
      )
    )

      // fetch All Locations
      FetchAllLocationsEffect$ = createEffect(() =>
        this.actions$.pipe(
          ofType(customersActions.FetchAllLocations),
          tap(() => {
            this.store.dispatch(customersActions.SetCustomersLoader({ isLoading: true }));
          }),
          switchMap(() =>
            this.customersServices.listAllLocations().pipe(
              map((res: APIResponse<ILocation>) => customersActions.FetchAllLocationsSuccess({ locations: res })),
              catchError(() => of(
                customersActions.FetchAllLocationsError()
              )),
              finalize(() => {
                this.store.dispatch(customersActions.SetCustomersLoader({ isLoading: false }));
              })
            )
          )
        )
      )
      // fetch Locations
      FetchLocationsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customersActions.FetchLocations),
      tap((action) => {
        if (!action.skipGlobalLoader) {
          this.store.dispatch(customersActions.SetCustomersLoader({ isLoading: true }));
        }
      }),
      switchMap((action) =>
          this.customersServices.listLocations(action.page, action.filter, action.key, action.sort_by, action.sort_mode).pipe(
            map((res: APIResponse<ILocation>) => customersActions.FetchLocationsSuccess({ locations: res })),
            catchError(() => of(
              customersActions.FetchLocationsError()
            )),
            finalize(() => {
              if (!action.skipGlobalLoader) {
                this.store.dispatch(customersActions.SetCustomersLoader({ isLoading: false }));
              }
            })
          ),

      ),
    )
  );

        // add Location
        AddLocationEffect$ = createEffect(() =>
          this.actions$.pipe(
            ofType(customersActions.AddLocation),
            tap(() => {
              this.store.dispatch(customersActions.SetCustomersLoader({ isLoading: true }));
            }),
            switchMap((action) =>
              this.customersServices.createLocation(action.payload).pipe(
                map((res: APIResponse<ILocation>) => customersActions.AddLocationSuccess({ location: res.object! })),
                tap(() => {
                  this.commonService.showToast(
                    this.translateService.instant(`alerts.${AlertEnums.companyAddSuccess}`),
                    'SUCCESS'
                  );
                }),
                catchError(() =>
                  of(
                    customersActions.AddLocationError()
                  )
                  // .pipe(
                  //   tap(() => {
                  //     this.commonService.showToast(
                  //       AlertEnums.EditError,
                  //       'ERROR'
                  //     )
                  //   }
                  //   )
                  // )
                ),
                finalize(() => {
                  this.store.dispatch(customersActions.SetCustomersLoader({ isLoading: false }));
                })
              )
            )
          )
        )

      // update Customer
      UpdateCustomerEffect$ = createEffect(() =>
        this.actions$.pipe(
          ofType(customersActions.UpdateCustomer),
          tap(() => {
            this.store.dispatch(customersActions.SetCustomersLoader({ isLoading: true }));
          }),
          switchMap((action) =>
            this.customersServices.updateCustomer(action.id, action.customer).pipe(
              map((res: APIResponse<ICustomer>) => customersActions.UpdateCustomerSuccess({ customer: res.data[0] })),
              tap(() => {
                this.commonService.showToast(
                  this.translateService.instant(`alerts.${AlertEnums.companyUpdateSuccess}`),
                  'SUCCESS'
                );
              }),
              catchError(() =>
                of(customersActions.UpdateCustomerError())
              ),
              finalize(() => {
                this.store.dispatch(customersActions.SetCustomersLoader({ isLoading: false }));
              })
            )
          )
        )
      )

      // approve Customer
      CustomerApprobationEffect$ = createEffect(() =>
        this.actions$.pipe(
          ofType(customersActions.CustomerApprobation),
          tap(() => {
            this.store.dispatch(customersActions.SetCustomersLoader({ isLoading: true }));
          }),
          switchMap((action) =>
            this.customersServices.CustomerApprobation(action.id, action.status).pipe(
              map((res: APIResponse<ICustomer>) => customersActions.CustomerApprobationSuccess({ customer: res.object as ICustomer })),
              tap(() => {
                this.commonService.showToast(
                  this.translateService.instant(`alerts.${AlertEnums.companyApproveSuccess}`),
                  'SUCCESS'
                );
              }),
              catchError(() =>
                of(
                  customersActions.CustomerApprobationError()
                )
              ),
              finalize(() => {
                this.store.dispatch(customersActions.SetCustomersLoader({ isLoading: false }));
              })
            )
          )
        )
      )

      // delete Customer
      deleteCustomerEffect$ = createEffect(() =>
        this.actions$.pipe(
          ofType(customersActions.DeleteCustomer),
          tap(() => {
            this.store.dispatch(customersActions.SetCustomersLoader({ isLoading: true }));
          }),
          switchMap((action) =>
            this.customersServices.deleteCustomer(action.id).pipe(
              map((res: APIResponse<ICustomer>) => customersActions.DeleteCustomerSuccess()),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.companyDeleteSuccess}`),
              'SUCCESS'
            );
          }),
          catchError(() =>
                of(
                  customersActions.DeleteCustomerError()
                )
                // .pipe(
                //   tap(() => {
                //     this.commonService.showToast(
                //       this.translateService.instant(`alerts.${AlertEnums.companyEditError}`),
                //       'ERROR'
                //     )
                //   })
                // )
              ),
              finalize(() => {
                this.store.dispatch(customersActions.SetCustomersLoader({ isLoading: false }));
              })
            )
          )
        )
      )

      // restore (reactivate) Customer
      restoreCustomerEffect$ = createEffect(() =>
        this.actions$.pipe(
          ofType(customersActions.RestoreCustomer),
          tap(() => {
            this.store.dispatch(customersActions.SetCustomersLoader({ isLoading: true }));
          }),
          switchMap((action) =>
            this.customersServices.restoreCustomer(action.id).pipe(
              map((res: APIResponse<ICustomer>) => customersActions.RestoreCustomerSuccess()),
              tap(() => {
                this.commonService.showToast(
                  this.translateService.instant(`alerts.${AlertEnums.companyRestoreSuccess}`),
                  'SUCCESS'
                );
              }),
              catchError(() =>
                of(
                  customersActions.RestoreCustomerError()
                )
              ),
              finalize(() => {
                this.store.dispatch(customersActions.SetCustomersLoader({ isLoading: false }));
              })
            )
          )
        )
      )

      // fetch All paymentTerms
      FetchAllpaymentTermsEffect$ = createEffect(() =>
        this.actions$.pipe(
          ofType(customersActions.FetchAllpaymentTerms),
          tap(() => {
            this.store.dispatch(customersActions.SetPaymentTermsLoader({ isLoading: true }));
          }),
          switchMap(() =>
            this.customersServices.listAllPaymentTerms().pipe(
              map((res: APIResponse<IPayment_term>) => customersActions.FetchAllpaymentTermsSuccess({ paymentTerms: res })),
              catchError(() => of(
                customersActions.FetchAllpaymentTermsError()
              )),
              finalize(() => {
                this.store.dispatch(customersActions.SetPaymentTermsLoader({ isLoading: false }));
              })
            )
          )
        )
      )

  // fetch All Countries
  FetchAllCountriesEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customersActions.FetchAllCountries),
      tap(() => {
        this.store.dispatch(customersActions.SetCountryLoader({ isLoading: true }));
      }),
      switchMap(() =>
        this.customersServices.listAllCountries().pipe(
          map((res: APIResponse<ICountry>) => customersActions.FetchAllCountriesSuccess({ countries: res })),
          catchError(() => of(
            customersActions.FetchAllCountriesError()
          )),
          finalize(() => {
            this.store.dispatch(customersActions.SetCountryLoader({ isLoading: false }))
          })
        )
      )
    )
  );

  // fetch All States
  FetchAllStatesEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customersActions.FetchAllStates),
      tap(() => {
        this.store.dispatch(customersActions.SetStatesLoader({ isLoading: true }))
      }),
      switchMap(() =>
        this.customersServices.listAllStates().pipe(
          map((res: APIResponse<IState>) => customersActions.FetchAllStatesSuccess({ states: res })),
          catchError(() => of(
            customersActions.FetchAllStatesError()
          )),
          finalize(() => {
            this.store.dispatch(customersActions.SetStatesLoader({ isLoading: false }))
          })
        )
      )
    )
  );

    // fetch All Currencies
    FetchAllCurrenciesEffect$ = createEffect(() =>
      this.actions$.pipe(
        ofType(customersActions.FetchAllCurrencies),
        tap(() => {
          this.store.dispatch(customersActions.SetCurrenciesLoader({ isLoading: true }))
        }),
        switchMap(() =>
          this.customersServices.listAllCurrencies().pipe(
            map((res: APIResponse<ICurrency>) => customersActions.FetchAllCurrenciesSuccess({ currencies: res })),
            catchError(() => of(
              customersActions.FetchAllCurrenciesError()
            )),
            finalize(() => {
              this.store.dispatch(customersActions.SetCurrenciesLoader({ isLoading: false }))
            })
          )
        ),
      )
    );

  // export
  exportCompanies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customersActions.exportCompanies),
      tap(() => {
        this.store.dispatch(customersActions.SetSearchCompanyLoader({ isLoading: true }))
      }),
      mergeMap((action) =>
        this.customersServices.exportCompanies(action.filter,action.key).pipe(
          map((file) => {
            const fileURL = window.URL.createObjectURL(file);
            const link = document.createElement('a');
            link.href = fileURL;
            link.download = `companies-${new Date().toLocaleString()}.xlsx`;
            link.click();
            return customersActions.exportCompaniesSuccess({ file });
          }),
          catchError((error) =>
            of(customersActions.exportCompaniesFailure({ error }))
          ),
          finalize(() => {
            this.store.dispatch(customersActions.SetSearchCompanyLoader({ isLoading: false }))
          })
        )
      )
    )
  );

  // Resend invitation
  resendInvitationEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customersActions.ResendInvitation),
      tap(() => {
        this.store.dispatch(customersActions.SetCustomersLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.customersServices.resendInvitation(action.contactId).pipe(
          map((res: APIResponse<any>) => customersActions.ResendInvitationSuccess({ response: res })),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.invitationResendSuccess}`),
              'SUCCESS'
            );
          }),
          catchError((error) => of(customersActions.ResendInvitationError({ error }))),
          finalize(() => {
            this.store.dispatch(customersActions.SetCustomersLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  deleteLocationContactEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customersActions.DeleteLocationContact),
      switchMap(({ locationId, contactId }) =>
        this.customersServices.deleteLocationContact(locationId, contactId).pipe(
          map(() => customersActions.DeleteLocationContactSuccess({ locationId, contactId })),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.contactDeleteSuccess}`),
              'SUCCESS'
            );
          }),
          catchError(() => of(customersActions.DeleteLocationContactError()))
        )
      )
    )
  );

  deleteLocationEffect$ = createEffect(() =>
      this.actions$.pipe(
        ofType(customersActions.DeleteLocation),
        tap(() => {
          this.store.dispatch(customersActions.SetCustomersLoader({ isLoading: true }));
        }),
        switchMap((action) =>
          this.customersServices.deleteLocation(action.id).pipe(
            map((res: APIResponse<ICustomer>) => customersActions.DeleteLocationSuccess()),
        tap(() => {
          this.commonService.showToast(
            this.translateService.instant(`alerts.${AlertEnums.companyDeleteSuccess}`),
            'SUCCESS'
          );
        }),
        catchError(() =>
              of(
                customersActions.DeleteLocationError()
              )
            ),
            finalize(() => {
              this.store.dispatch(customersActions.SetCustomersLoader({ isLoading: false }));
            })
          )
        )
      )
    )

  UpdateLocationEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(customersActions.UpdateLocation),
      tap(() => {
        this.store.dispatch(customersActions.SetCustomersLoader({ isLoading: true }));
      }),
      mergeMap((action) =>
        this.customersServices.updateLocation(action.id, action.payload).pipe(
          map((res: APIResponse<ILocation>) =>
            customersActions.UpdateLocationSuccess({ location: res.object! })
          ),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.locationUpdateSuccess}`),
              'SUCCESS'
            );
          }),
          catchError(() => of(customersActions.UpdateLocationError())),
          finalize(() => {
            this.store.dispatch(customersActions.SetCustomersLoader({ isLoading: false }));
          })
        )
      )
    )
  );
}
